// Angular modules
import { InjectionToken, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { Route, RouterModule } from '@angular/router';

// Third party modules
import { MissingTranslationHandler, MissingTranslationHandlerParams, TranslateModule } from '@ngx-translate/core';

// Own modules
import { Diagnostic } from '@lmnop/web-core';

// Angular core module
import { NavbarControl } from './components/control-components/navbar/navbar.control';

import { DataTablesModule } from 'angular-datatables';
import { AngularTranslation } from './ioc/implementation/angular-translation/angular-translation.class';

import { ContactFormControl } from './components/control-components/contact-form/contact-form.control';
import { DontForgetToSmileControl } from './components/control-components/dont-forget-to-smile/dont-forget-to-smile.control';
import { DownloadExtensionControl } from './components/control-components/download-extension/download-extension.control';
import { FieldErrorDisplayControl } from './components/control-components/field-error-display/field-error-display.control';
import { GearLoadingControl } from './components/control-components/gear-loading/gear-loading.control';
import { GotoUrlControl } from './components/control-components/goto-url/goto-url.control';
import { I18nHtmlContentLoaderControl } from './components/control-components/i18n-html-content-loader/i18n-html-content-loader.control';
import { BugReportFormControl } from './components/control-components/bug-report-form/bug-report-form.control';
import { SiteUrlNavigateNavbarItem } from './components/control-components/navbar/navbar-item.classes';
import { NewsCountControl } from './components/control-components/news/news-count/news-count.control';
import { NewsListControl } from './components/control-components/news/news-list/news-list.control';
import { NightModeOnOffControl } from './components/control-components/night-mode-on-off/night-mode-on-off.control';
import { PageFooterControl } from './components/control-components/page-footer/page-footer.control';
import { PaypalDonationControl } from './components/control-components/paypal-donation/paypal-donation.control';
import { ResetDefaultSettingsControl } from './components/control-components/reset-default-settings/reset-default-settings.control';
import { ShareButtonsControl } from './components/control-components/share-buttons/share-buttons.control';
import { SupportButtonsControl } from './components/control-components/support-buttons/support-buttons.control';
import { ToggleButtonControl } from './components/control-components/toggle-button/toggle-button.control';
import { VersionControl } from './components/control-components/version/version.control';
import { VideoLinkControl } from './components/control-components/video-link/video-link.control';
import { WecdevLeftBarControl } from './components/control-components/wecdev-left-bar/wecdev-left-bar.control';
import { DeleteComponent } from './components/datatable-components/delete.component';
import { EditDeleteComponent } from './components/datatable-components/edit-delete.component';
import { EditComponent } from './components/datatable-components/edit.component';
import { IsDefaultCheckBoxComponent } from './components/datatable-components/is-default-checkbox.component';
import { IsInvalidComponent } from './components/datatable-components/is-invalid.component';
import { NotificationStatusComponent } from './components/datatable-components/notification-status.component';
import { OpenDeleteComponent } from './components/datatable-components/open-delete.component';
import { CheckboxComponent } from './components/datatable-components/checkbox.component';
import { NumberInputComponent } from './components/datatable-components/number-input.component';
import { OpenNotificationComponent } from './components/datatable-components/open-notification.component';
import { SearchBoxComponent } from './components/datatable-components/search-box.component';
import { ViewComponent } from './components/datatable-components/view.component';
import { AskShareUsFormModal } from './components/modal-components/ask-share-us-form/ask-share-us-form.modal';
import { DeleteConfirmModal } from './components/modal-components/delete-confirm-modal/delete-confirm-modal';
import { ForbiddenPage } from './components/page-components/forbidden/forbidden.page';
import { NotFoundPage } from './components/page-components/not-found/not-found.page';
import { ClickWaitDirective } from './directives/click-wait.directive';
import { AngularErrorHandler } from './ioc/implementation/angular-error-handler/angular-error-handler.class';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { LanguageService } from './services/language.service';

import { CheckboxExtendedControl } from './components/extended-form-control-components/checkbox-extended/checkbox-extended.control';
import { DateExtendedControl } from './components/extended-form-control-components/date-extended/date-extended.control';
import { FileExtendedControl } from './components/extended-form-control-components/file-extended/file-extended.control';
import { HiddenExtendedControl } from './components/extended-form-control-components/hidden-extended/hidden-extended.control';
import { MultiselectExtendedControl } from './components/extended-form-control-components/multiselect-extended/multiselect-extended.control';
import { NumberExtendedControl } from './components/extended-form-control-components/number-extended/number-extended.control';
import { PasswordExtendedControl } from './components/extended-form-control-components/password-extended/password-extended.control';
import { SelectExtendedControl } from './components/extended-form-control-components/select-extended/select-extended.control';
import { SummernoteTextboxExtendedControl } from './components/extended-form-control-components/summernote-textbox-extended/summernote-textbox-extended.control';
import { TextExtendedControl } from './components/extended-form-control-components/text-extended/text-extended.control';
import { TextareaExtendedControl } from './components/extended-form-control-components/textarea-extended/textarea-extended.control';
import { AuthSubscriptionService } from './services/auth-subscription.service';
import { FormatDateService } from './services/format-date.service';
import { MenuBaseService } from './services/menu-base.service';
import { PdfPreviewService } from './services/pdf-preview.service';
import { SignalRService } from './services/signalr-service';

import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { LabelExtendedControl } from './components/extended-form-control-components/label-extended/label-extended.control';
import { ButtonControl } from './components/control-components/button/button.control';
import { PlusButtonControl } from './components/control-components/button/special-buttons/plus-button.control';
import { ImportButtonControl } from './components/control-components/button/special-buttons/import-button.control';
import { ExportButtonControl } from './components/control-components/button/special-buttons/export-button.control';
import { IconButtonControl } from './components/control-components/button/special-buttons/icon-button.control';
import { ColorExtendedControl } from './components/extended-form-control-components/color-extended/color-extended.control';
import { YourBannerIsHereControl } from './components/control-components/your-banner-is-here/your-banner-is-here.control';
import { DomainForSaleListTab } from './components/control-components/domain-for-sale-list/domain-for-sale-list.control';
import { ProgrammingAdvertisementTab } from './components/control-components/programming-advertisement/programming-advertisement.control';
import { SuggestionControl } from './components/control-components/suggestion/suggestion.control';
import { ColorSimpleControl } from './components/simple-input-components/color-simple/color-simple.control';
import { HtmlViewerControl } from './components/control-components/html-viewer/html-viewer.control';

import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { PopoverModule } from 'ngx-bootstrap/popover';

// tslint:disable-next-line: typedef
export const TRANSLATE_FILES_DIRECTORY__INJECTION_TOKEN = new InjectionToken<string>('TRANSLATE_FILES_DIRECTORY');
export const MODULE_ENVIRONMENTS = new InjectionToken<any>('MODULE_ENVIRONMENTS');
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL');

const PUBLIC_ANGULARCOREMODULE_DECLARATIONS: any[] = [
    //components
    ContactFormControl,
    SupportButtonsControl,
    VersionControl,
    ShareButtonsControl,
    DontForgetToSmileControl,
    GotoUrlControl,
    NightModeOnOffControl,
    ResetDefaultSettingsControl,
    GearLoadingControl,
    I18nHtmlContentLoaderControl,
    BugReportFormControl,
    NavbarControl,
    DownloadExtensionControl,
    PageFooterControl,
    PaypalDonationControl,
    FieldErrorDisplayControl,
    ToggleButtonControl,
    NewsListControl,
    NewsCountControl,
    VideoLinkControl,
    WecdevLeftBarControl,
    ButtonControl,
    PlusButtonControl,
    ImportButtonControl,
    ExportButtonControl,
    IconButtonControl,
    YourBannerIsHereControl,
    DomainForSaleListTab,
    ProgrammingAdvertisementTab,
    SuggestionControl,
    HtmlViewerControl,
    //modals
    AskShareUsFormModal,
    DeleteConfirmModal,
    //pages
    NotFoundPage,
    ForbiddenPage,
    //pipes
    SafeHtmlPipe,
    SafeUrlPipe,
    //directives
    ClickWaitDirective,
    //datatable components
    DeleteComponent,
    EditDeleteComponent,
    EditComponent,
    IsDefaultCheckBoxComponent,
    IsInvalidComponent,
    NotificationStatusComponent,
    OpenDeleteComponent,
    CheckboxComponent,
    NumberInputComponent,
    OpenNotificationComponent,
    SearchBoxComponent,
    ViewComponent,
    //extended form-control components
    SelectExtendedControl,
    TextareaExtendedControl,
    TextExtendedControl,
    NumberExtendedControl,
    PasswordExtendedControl,
    DateExtendedControl,
    CheckboxExtendedControl,
    FileExtendedControl,
    HiddenExtendedControl,
    SummernoteTextboxExtendedControl,
    MultiselectExtendedControl,
    LabelExtendedControl,
    ColorExtendedControl,
    //simple-input-control components
    ColorSimpleControl,
    //base component
    //BaseComponent
];

@NgModule({
    imports: [BrowserModule, FormsModule, TranslateModule, RouterModule, ReactiveFormsModule, DataTablesModule, NgMultiSelectDropDownModule.forRoot(), BsDatepickerModule.forRoot(), PopoverModule],
    declarations: [PUBLIC_ANGULARCOREMODULE_DECLARATIONS],
    exports: [PUBLIC_ANGULARCOREMODULE_DECLARATIONS],
    providers: [AngularErrorHandler, LanguageService, AngularTranslation, AuthSubscriptionService, PdfPreviewService, SignalRService, FormatDateService, MenuBaseService],
})
export class AngularCoreModule {}

export class AppMissingTranslationHandler implements MissingTranslationHandler {
    handle(params: MissingTranslationHandlerParams): string {
        if (!params.translateService['pending']) {
            Diagnostic.saveErrorToDom(true, 'Missing translation: NO_TRANSLATION_' + params.key);
        }
        return 'NO_TRANSLATION_' + params.key;
    }
}

export const Routes: Route[] = [
    { path: '403', component: ForbiddenPage },
    { path: '404', component: NotFoundPage },
    { path: '**', redirectTo: '404' },
];

export function setAngularCoreModuleNavbar(url: string, navbarComponent: NavbarControl): void {
    if (url.toLowerCase().includes('404')) {
        navbarComponent.items = [new SiteUrlNavigateNavbarItem('return_to_home', 'home')];
    }
    if (url.toLowerCase().includes('403')) {
        navbarComponent.items = [new SiteUrlNavigateNavbarItem('return_to_home', 'home')];
    }
}

//ioc
export * from './ioc/implementation/angular-core-ioc-container/angular-core-ioc-container.class';
export * from './ioc/implementation/angular-core-ioc-container/angular-core-ioc-container.interface';
export * from './ioc/ioc.keys.class';

export * from './ioc/implementation/angular-error-handler/angular-error-handler.interface';
export * from './ioc/implementation/angular-translation/angular-translation.interface';
//this class need to be exported because it is used durig angular app initialization
export * from './ioc/implementation/angular-error-handler/angular-error-handler.class';

//base classes
export * from './base-classes/extended-form-control.class';
export * from './base-classes/extended-form-group.class';
export * from './base-classes/form-field-classes/checkbox-control.class';
export * from './base-classes/form-field-classes/date-control.class';
export * from './base-classes/form-field-classes/hidden-control.class';
export * from './base-classes/form-field-classes/multiselect-control.class';
export * from './base-classes/form-field-classes/number-control.class';
export * from './base-classes/form-field-classes/password-control.class';
export * from './base-classes/form-field-classes/select-control.class';
export * from './base-classes/form-field-classes/summernote-textbox-control.class';
export * from './base-classes/form-field-classes/text-control.class';
export * from './base-classes/form-field-classes/textarea-control.class';

export * from './base-classes/primitive-value-wrappers.classes';

//components - controls
export * from './components/control-components/contact-form/contact-form.control';
export * from './components/control-components/dont-forget-to-smile/dont-forget-to-smile.control';
export * from './components/control-components/download-extension/download-extension.control';
export * from './components/control-components/field-error-display/field-error-display.control';
export * from './components/control-components/gear-loading/gear-loading.control';
export * from './components/control-components/goto-url/goto-url.control';
export * from './components/control-components/i18n-html-content-loader/i18n-html-content-loader.control';
export * from './components/control-components/bug-report-form/bug-report-form.control';
export * from './components/control-components/navbar/navbar-item.classes';
export * from './components/control-components/navbar/navbar.control';
export * from './components/control-components/news/news-count/news-count.control';
export * from './components/control-components/news/news-list/news-list.control';
export * from './components/control-components/night-mode-on-off/night-mode-on-off.control';
export * from './components/control-components/page-footer/page-footer.control';
export * from './components/control-components/paypal-donation/paypal-donation.control';
export * from './components/control-components/reset-default-settings/reset-default-settings.control';
export * from './components/control-components/share-buttons/share-buttons.control';
export * from './components/control-components/support-buttons/support-buttons.control';
export * from './components/control-components/toggle-button/toggle-button.control';
export * from './components/control-components/version/version.control';
export * from './components/control-components/video-link/video-link.control';
export * from './components/control-components/wecdev-left-bar/wecdev-left-bar.control';
export * from './components/control-components/button/button.control';
export * from './components/control-components/button/special-buttons/plus-button.control';
export * from './components/control-components/button/special-buttons/import-button.control';
export * from './components/control-components/button/special-buttons/export-button.control';
export * from './components/control-components/button/special-buttons/icon-button.control';
export * from './components/control-components/your-banner-is-here/your-banner-is-here.control';
export * from './components/control-components/programming-advertisement/programming-advertisement.control';
export * from './components/control-components/domain-for-sale-list/domain-for-sale-list.control';
export * from './components/control-components/suggestion/suggestion.control';
export * from './components/control-components/html-viewer/html-viewer.control';

export * from './components/base/abstract-modal-base/abstract-modal-base.component';
export * from './components/base/base/base-component.component';

//components - modals
export * from './components/modal-components/ask-share-us-form/ask-share-us-form.modal';
export * from './components/modal-components/delete-confirm-modal/delete-confirm-modal';

//components - pages
export * from './components/page-components/forbidden/forbidden.page';
export * from './components/page-components/not-found/not-found.page';

//components - datatable
export * from './components/datatable-components/delete.component';
export * from './components/datatable-components/edit-delete.component';
export * from './components/datatable-components/edit.component';
export * from './components/datatable-components/is-default-checkbox.component';
export * from './components/datatable-components/is-invalid.component';
export * from './components/datatable-components/notification-status.component';
export * from './components/datatable-components/open-delete.component';
export * from './components/datatable-components/open-notification.component';
export * from './components/datatable-components/search-box.component';
export * from './components/datatable-components/checkbox.component';
export * from './components/datatable-components/number-input.component';
export * from './components/datatable-components/view.component';

//components - extended form control components
export * from './components/extended-form-control-components/checkbox-extended/checkbox-extended.control';
export * from './components/extended-form-control-components/date-extended/date-extended.control';
export * from './components/extended-form-control-components/file-extended/file-extended.control';
export * from './components/extended-form-control-components/hidden-extended/hidden-extended.control';
export * from './components/extended-form-control-components/label-extended/label-extended.control';
export * from './components/extended-form-control-components/multiselect-extended/multiselect-extended.control';
export * from './components/extended-form-control-components/number-extended/number-extended.control';
export * from './components/extended-form-control-components/password-extended/password-extended.control';
export * from './components/extended-form-control-components/select-extended/select-extended.control';
export * from './components/extended-form-control-components/summernote-textbox-extended/summernote-textbox-extended.control';
export * from './components/extended-form-control-components/text-extended/text-extended.control';
export * from './components/extended-form-control-components/textarea-extended/textarea-extended.control';
export * from './components/extended-form-control-components/color-extended/color-extended.control';

//components - simple input control components
export * from './components/simple-input-components/color-simple/color-simple.control';

//pipes
export * from './pipes/safe-html.pipe';
export * from './pipes/safe-url.pipe';

//services
export * from './services/auth-subscription.service';
export * from './services/format-date.service';
export * from './services/language.service';
export * from './services/menu-base.service';
export * from './services/pdf-preview.service';
export * from './services/signalr-service';
export * from './services/translated-toastr.service';
export * from './services/validation-handler.service';

//classes
export * from './classes/current-tabpage-storage-wrapper.class';
export * from './classes/datatable-classes/column-data.class';
export * from './classes/datatable-classes/datatable-request.class';
export * from './classes/datatable-classes/datatable-result.class';
export * from './classes/datatable-classes/one-column-search-data.class';
export * from './classes/datatable-classes/order-data.class';
export * from './classes/datatable-classes/search-data.class';
export * from './classes/local-storage-wrapper.class';

//directives
export * from './directives/click-wait.directive';

//validators
export * from './validation';

//pocos
export * from './components/control-components/news/pocos/news.class';

//claim types
export * from './claim-types';

//enums
export * from './enums/language-id.enum';

//guards
export * from './guards/auth.guard';
export * from './guards/no-auth.guard';

//tokens
export * from './tokens/injection-tokens';

//interceptors
export * from './interceptors/auth.interceptor';
export * from './interceptors/http-error-handler.service';
export * from './interceptors/signalr-header.interceptor';

//types
export * from './types/language-list.model';
export * from './types/settings-backup.model';
export * from './types/base-response.model';
export * from './types/web-page-area.model';

//interfaces
export * from './interfaces/core-service.interface';
export * from './interfaces/authentication-service.interface';
export * from './interfaces/site-settings.interface';
